import { Component, ChangeDetectionStrategy, OnInit, EventEmitter, Input, Output, OnDestroy } from "@angular/core";
import { FormControl } from "@angular/forms";
import { Subscription } from "rxjs";

import { QuantityChangedEvent } from '../../models/quantity-changed-event';
import { FlyToCartService } from "../../services/fly-to-cart.service";
import { filter } from 'rxjs/operators';

@Component({
  selector: 'app-quantity-picker',
  templateUrl: 'quantity-picker.component.html',
  styleUrls: ['quantity-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class QuantityPickerComponent implements OnInit, OnDestroy {
  relativeQuantity: number
  absoluteQuantity: number
  quantityInput = new FormControl()
  inputSubscription: Subscription

  @Input() value: number = 0
  @Input() stepping: number = 1
  @Input() minQuantity: number = 1
  @Input() itemId: string
  @Input() source: string
  @Input() mode: string = 'catalog'
  @Input() viewType: string
  @Input() quantityType: string = 'relative'

  @Output() quantityChanged = new EventEmitter<QuantityChangedEvent>()

  constructor(private flyToCartSvc: FlyToCartService) { }

  ngOnInit() {
    this.inputSubscription = this.quantityInput.valueChanges.pipe(
      filter(value => Number(value) > 0)
    ).subscribe(value => {
        this.relativeQuantity = value
        this.absoluteQuantity = value * this.stepping

        // Directly emit to API if in checkout mode AND value of inputfield is different than value of @Input component
        if (this.checkoutMode && (value !== (this.value / this.stepping))) {
          // Use setTimout() to give user time to finish typing (for example 10, or 100)
          setTimeout(() => {
            this.emitQuantity(this.absoluteQuantity)
          }, 1000)
        }
      })

    this.setQuantity()
  }

  ngOnDestroy() {
    this.inputSubscription.unsubscribe()
  }

  get checkoutMode(): boolean {
    return this.mode.toLowerCase() === 'checkout'
  }

  get unit(): number {
    return this.stepping || 1
  }

  get showAbsoluteQuantity(): boolean {
    return !this.checkoutMode && this.quantityType === 'relative'
  }

  plus(): void {
    this.relativeQuantity++
    this.quantityInput.setValue(this.relativeQuantity)
  }

  minus(): void {
    if (this.relativeQuantity - 1 >= 1) {
      this.relativeQuantity--
      this.quantityInput.setValue(this.relativeQuantity)
    } else {
      this.setQuantity()
    }
  }

  addToCart(): void {
    this.emitQuantity(this.absoluteQuantity)
    this.flyToCartSvc.animateFlyToCart(this.itemId, this.source)
    this.setQuantity()
  }

  private emitQuantity(absoluteQuantity: number): void {
    if (!this.checkoutMode) {
      absoluteQuantity = absoluteQuantity + this.value
    }

    this.quantityChanged.emit({
      quantity: absoluteQuantity,
      isEdited: false
    })
  }

  private setQuantity(): void {
    this.relativeQuantity = 1

    // If in checkoutMode, display the number of items currently in cart.
    if (this.checkoutMode) {
      this.relativeQuantity = this.value / this.stepping
    }

    this.absoluteQuantity = this.relativeQuantity * this.stepping
    this.quantityInput.setValue(this.relativeQuantity)
  }
}
